export function formatMoney(amount: string | number | undefined, currency = 'EUR'): string {
  if (amount === undefined) return '—';
  const numeric = typeof amount === 'number' ? amount : Number(amount);
  if (!Number.isFinite(numeric)) return `${amount} ${currency}`;
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency }).format(numeric);
}

export function formatDate(value: string | undefined): string {
  if (!value) return '—';
  const parsed = new Date(value.length === 10 ? `${value}T00:00:00` : value);
  return Number.isNaN(parsed.getTime())
    ? value
    : new Intl.DateTimeFormat('it-IT', { dateStyle: 'medium' }).format(parsed);
}

export function formatDateTime(value: string | undefined): string {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? value
    : new Intl.DateTimeFormat('it-IT', { dateStyle: 'short', timeStyle: 'short' }).format(parsed);
}

export function formatDuration(seconds: number): string {
  const safe = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const remaining = safe % 60;
  return [hours, minutes, remaining].map((part) => String(part).padStart(2, '0')).join(':');
}

export function statusTone(
  status: string
): 'neutral' | 'purple' | 'success' | 'warning' | 'danger' {
  if (
    ['ACTIVE', 'APPROVED', 'PAID', 'DONE', 'WON', 'COMPLETED', 'DELIVERED', 'REIMBURSED'].includes(
      status
    )
  )
    return 'success';
  if (['OVERDUE', 'URGENT', 'REJECTED', 'LOST', 'CANCELLED', 'FAILED'].includes(status))
    return 'danger';
  if (['PENDING', 'IN_PROGRESS', 'ON_HOLD', 'SUSPENDED', 'EXPECTED'].includes(status))
    return 'warning';
  if (['PLANNED', 'TODO', 'OPEN'].includes(status)) return 'purple';
  return 'neutral';
}

const statusLabels: Record<string, string> = {
  ACTIVE: 'Attivo',
  INACTIVE: 'Inattivo',
  PROSPECT: 'Potenziale',
  ARCHIVED: 'Archiviato',
  PLANNED: 'Pianificato',
  INBOX: 'Da organizzare',
  IN_PROGRESS: 'In corso',
  BLOCKED: 'Bloccato',
  ON_HOLD: 'In pausa',
  COMPLETED: 'Completato',
  CANCELLED: 'Annullato',
  RUNNING: 'In esecuzione',
  PAUSED: 'In pausa',
  TODO: 'Da fare',
  DOING: 'In lavorazione',
  DONE: 'Completato',
  OPEN: 'Aperta',
  WON: 'Vinta',
  LOST: 'Persa',
  LEAD: 'Contatto iniziale',
  QUALIFIED: 'Qualificata',
  PROPOSAL: 'Proposta',
  PROPOSAL_SENT: 'Proposta inviata',
  NEGOTIATION: 'Negoziazione',
  LOW: 'Bassa',
  NORMAL: 'Normale',
  MEDIUM: 'Media',
  HIGH: 'Alta',
  URGENT: 'Urgente',
  PENDING: 'In attesa',
  APPROVED: 'Approvata',
  REJECTED: 'Rifiutata',
  REIMBURSED: 'Rimborsata',
  NOT_APPLICABLE: 'Non applicabile',
  NOT_REQUIRED: 'Non richiesto',
  EXPECTED: 'Prevista',
  PAID: 'Pagata',
  OVERDUE: 'Scaduta',
  SUSPENDED: 'Sospeso',
  FAILED: 'Non riuscita',
  SENT: 'Inviata',
  DELIVERED: 'Consegnata',
  RETRY: 'Nuovo tentativo previsto',
  DEAD_LETTER: 'Consegna interrotta',
  SENDING: 'Invio in corso',
  RECEIVABLE: 'Da incassare',
  PAYABLE: 'Da pagare',
  DRAFT: 'Bozza',
  ONCE: 'Una tantum',
  WEEKLY: 'Settimanale',
  MONTHLY: 'Mensile',
  QUARTERLY: 'Trimestrale',
  SEMIANNUAL: 'Semestrale',
  YEARLY: 'Annuale',
  ANNUAL: 'Annuale',
  CUSTOM: 'Personalizzata'
};

export function humanStatus(status: string): string {
  return (
    statusLabels[status] ??
    status
      .toLocaleLowerCase('it')
      .replaceAll('_', ' ')
      .replace(/^./, (character) => character.toLocaleUpperCase('it'))
  );
}
